import { MentorsSelect } from "@/components/dashboard/mentors-select";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { useAssignSupervisorMentor } from "@/lib/hooks/mentors/use-assign-supervisor-mentors";
import { cn } from "@/lib/utils";
import {
  CheckCircle2Icon,
  Loader2Icon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";

interface AssignMentorModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  assignedMentors: Array<{ id: string; name: string }>;
}

interface OperationStatus {
  mentorId: string;
  mentorName: string;
  status: "pending" | "success" | "error";
  error?: string;
}

export function AssignMentorModal({
  isOpen,
  onClose,
  userId,
  assignedMentors,
}: AssignMentorModalProps) {
  const [selectedMentors, setSelectedMentors] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [operationStatuses, setOperationStatuses] = useState<OperationStatus[]>(
    [],
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const { mutateAsync: assignMentor } = useAssignSupervisorMentor();
  const { toast } = useToast();

  const handleClose = () => {
    if (!isProcessing) {
      setSelectedMentors([]);
      setOperationStatuses([]);
      onClose();
    }
  };

  const processAssignments = async () => {
    setIsProcessing(true);
    setOperationStatuses(
      selectedMentors.map((mentor) => ({
        mentorId: mentor.id,
        mentorName: mentor.name,
        status: "pending",
      })),
    );

    let successCount = 0;
    let failureCount = 0;

    for (const mentor of selectedMentors) {
      try {
        await assignMentor({ mentorId: mentor.id, supervisorId: userId });
        setOperationStatuses((prev) =>
          prev.map((status) =>
            status.mentorId === mentor.id
              ? { ...status, status: "success" }
              : status,
          ),
        );
        successCount++;
      } catch (error) {
        setOperationStatuses((prev) =>
          prev.map((status) =>
            status.mentorId === mentor.id
              ? { ...status, status: "error", error: "Failed to assign mentor" }
              : status,
          ),
        );
        failureCount++;
      }
    }

    setIsProcessing(false);
    toast({
      title: "Batch Assignment Complete",
      description: `Successfully assigned ${successCount} mentors. ${failureCount > 0 ? `Failed: ${failureCount}` : ""}`,
      variant: failureCount > 0 ? "destructive" : "default",
    });

    if (failureCount === 0) {
      handleClose();
    }
  };

  const progress =
    operationStatuses.length > 0
      ? (operationStatuses.filter((s) => s.status === "success").length /
          operationStatuses.length) *
        100
      : 0;

  const showAssignButton =
    selectedMentors.length > 0 &&
    !isProcessing &&
    operationStatuses.length === 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Assign New Mentors</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 py-4">
          <MentorsSelect
            mentorId={null}
            handleFilterChange={(_, value, label) => {
              if (value && label) {
                const mentorExists = selectedMentors.some(
                  (m) => m.id === value,
                );
                if (!mentorExists) {
                  setSelectedMentors((prev) => [
                    ...prev,
                    { id: value, name: label },
                  ]);
                }
              }
            }}
            isMulti
            selectedMentors={selectedMentors}
            assignedMentors={assignedMentors}
          />
          {selectedMentors.length > 0 && operationStatuses.length === 0 && (
            <div className="mt-4">
              <div className="mb-2 text-sm font-medium">Selected Mentors:</div>
              <ScrollArea className="h-[120px]">
                <div className="space-y-1 pr-4">
                  {selectedMentors.map((mentor) => (
                    <div
                      key={mentor.id}
                      className="flex items-center justify-between rounded bg-gray-50 p-2 text-sm"
                    >
                      <span>{mentor.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() =>
                          setSelectedMentors((prev) =>
                            prev.filter((m) => m.id !== mentor.id),
                          )
                        }
                      >
                        <XIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
        {operationStatuses.length > 0 && (
          <div className="mb-4">
            <Progress value={progress} className="mb-4" />
            <ScrollArea className="h-[200px]">
              <div className="space-y-2 pr-4">
                {operationStatuses.map((status) => (
                  <div
                    key={status.mentorId}
                    className={cn(
                      "flex items-center justify-between rounded p-2",
                      status.status === "success" && "bg-green-50",
                      status.status === "error" && "bg-red-50",
                    )}
                  >
                    <span className="text-sm">{status.mentorName}</span>
                    <div className="flex items-center">
                      {status.status === "pending" && (
                        <Loader2Icon className="h-4 w-4 animate-spin text-gray-500" />
                      )}
                      {status.status === "success" && (
                        <CheckCircle2Icon className="h-4 w-4 text-green-500" />
                      )}
                      {status.status === "error" && (
                        <XCircleIcon className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
        <div className="flex justify-end space-x-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
          >
            {isProcessing ? "Please wait..." : "Cancel"}
          </Button>
          {showAssignButton && (
            <Button
              onClick={processAssignments}
              disabled={selectedMentors.length === 0 || isProcessing}
            >
              {isProcessing && (
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              )}
              Assign{" "}
              {selectedMentors.length > 0 ? `(${selectedMentors.length})` : ""}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
